import { BoardCommand } from './assignment/types';

/**
 * 計測 1 件ぶん。画面側が `screen_name` を足して `ReactGA.event` へ渡す。
 *
 * Why not ここで `ReactGA.event` を撃つ: `screen_name` は画面ごとに違い、同じ盤面の
 * 部品を長距離配車とカード配車の両方が使う。純粋な値にしておけば、どちらの画面から
 * 呼んでも正しい画面名が付き、テストもモック無しで書ける。
 */
export type DragAnalyticsEvent = {
  action: string;
  buttonName: string;
  /** 対象の件数や種類。V3Plans の `label` と同じ使い方 */
  label?: string | number;
};

/** 掴んだもの。画面が知っているので値で受ける */
export type DraggedKind = 'unassigned' | 'trip' | 'card' | 'vehicle';

const DRAGGED_LABELS: Record<DraggedKind, string> = {
  unassigned: '未割当カード',
  trip: '運行バー',
  card: '配車カード',
  vehicle: '車両行',
};

/**
 * 掴んだとき。
 *
 * 落とさずにやめた回数は、これと `dragEndAnalytics` の件数の差で読める。
 * 中断そのものを別イベントにはしない（ESC・枠外へのドロップ・掴み直しで二重に数える）。
 */
export function dragStartAnalytics(kind: DraggedKind): DragAnalyticsEvent {
  return { action: 'drag_start', buttonName: 'D&D 開始', label: DRAGGED_LABELS[kind] };
}

/**
 * 落としたとき。`buildBoardCommand` が決めた操作の種類をそのまま action にする。
 *
 * action を `click` に寄せないのは、GA 側で「ドラッグ操作だけ」を切り出せるようにするため
 * （ユーザー判断 2026-10-01）。既存の `executePlanning` と同じ動詞型の命名に倣う。
 *
 * Why not 画面ごとに判定を書く: 盤面の D&D は長距離配車もカード配車も同じ
 * `BoardCommand` に落ちる（`assignment/types.ts`）。片方だけ文言が変わると、
 * GA のレポートで 2 画面を並べられなくなる。
 */
export function dragEndAnalytics(command: BoardCommand | null): DragAnalyticsEvent | null {
  if (!command) return null;

  if (command.kind === 'reorder') {
    return { action: 'drag_reorder', buttonName: 'D&D 行内の並べ替え' };
  }
  if (command.kind === 'unassign') {
    return {
      action: 'drag_unassign',
      buttonName: 'D&D 未割当へ戻す',
      label: command.legRunIds.length,
    };
  }
  // 未割当からの投入だけが「割り付けた」。盤面の中で動かしたものは移動として分ける
  return command.source.scope === 'unassigned'
    ? { action: 'drag_assign', buttonName: 'D&D 割り付け', label: command.legRunIds.length }
    : { action: 'drag_move', buttonName: 'D&D 他レーンへ移動', label: command.legRunIds.length };
}
